import type { SimpleGridProps, UseRadioProps } from '@hover/blueprint';
import { Flex, RadioControl, system, useRadio } from '@hover/blueprint';
import React from 'react';

import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import type { SkeletonProps } from 'src/shared/components/Skeleton/Skeleton';

export interface PaletteCardProps
  extends Omit<SimpleGridProps, keyof UseRadioProps>,
    UseRadioProps {
  name: string;
  isLoading?: boolean;
  thumbnailProps?: SkeletonProps;
}

const defaultSize = {
  width: { base: '11rem', tablet: '13rem' },
  height: { base: '15rem', tablet: '19rem' },
} satisfies Partial<PaletteCardProps>;

const PaletteCard = ({
  children,
  name,
  isLoading = false,
  thumbnailProps = {},
  ...props
}: PaletteCardProps) => {
  const { getLabelProps, getRootProps, getCheckboxProps, getInputProps } =
    useRadio({ ...props, value: name });

  return (
    <Flex
      as="label"
      borderRadius={350}
      columns={3}
      cursor="pointer"
      direction="column"
      gap={0}
      overflow="hidden"
      shadow="distance300"
      {...defaultSize}
      {...getRootProps()}
      {...props}
    >
      <Skeleton
        bg="neutral.50"
        height="100%"
        isLoaded={!isLoading}
        {...thumbnailProps}
      >
        {children}
      </Skeleton>
      <Flex
        alignItems="center"
        boxSizing="border-box"
        direction="row"
        display="flex"
        gap={200}
        padding={{ base: 400, tablet: 500 }}
        width="100%"
      >
        <RadioControl
          inputProps={getInputProps()}
          radioProps={getCheckboxProps()}
          variant="filled"
        />
        <Skeleton
          borderRadius="200"
          isLoaded={!isLoading}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <system.span {...getLabelProps()}>{name}</system.span>
        </Skeleton>
      </Flex>
    </Flex>
  );
};

export { PaletteCard };
